import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { DemoStatus, ProspectOutcome } from "@mm-sales/shared";
import { Clock } from "lucide-react";
import { useEffect, useState } from "react";

export const DemoStatusBadge = ({ status }: { status: DemoStatus }) => {
  const map: Record<
    DemoStatus,
    { v: "success" | "warning" | "destructive" | "secondary"; l: string }
  > = {
    active: { v: "success", l: "Active" },
    provisioning: { v: "warning", l: "Building" },
    expired: { v: "secondary", l: "Expired" },
    revoked: { v: "destructive", l: "Revoked" },
    failed: { v: "destructive", l: "Failed" },
  };
  return <Badge variant={map[status].v}>{map[status].l}</Badge>;
};

export const OutcomeBadge = ({ outcome }: { outcome: ProspectOutcome }) => {
  const map: Record<
    ProspectOutcome,
    { v: "success" | "warning" | "destructive" | "default"; l: string }
  > = {
    open: { v: "default", l: "Open" },
    won: { v: "success", l: "Won" },
    lost: { v: "destructive", l: "Lost" },
    missed: { v: "warning", l: "Missed" },
  };
  return <Badge variant={map[outcome].v}>{map[outcome].l}</Badge>;
};

/** "59:32" style countdown for sessions, or "6d 4h" for demos. */
export function Countdown({
  to,
  compact,
  className,
}: { to: string; compact?: boolean; className?: string }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const ms = Math.max(0, new Date(to).getTime() - Date.now());
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const text =
    d > 0
      ? `${d}d ${h}h`
      : h > 0
        ? `${h}h ${String(m).padStart(2, "0")}m`
        : `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 tabular-nums",
        ms === 0 && "text-destructive",
        className,
      )}
    >
      {!compact ? <Clock className="h-4 w-4" /> : null}
      {text}
    </span>
  );
}

/** Ring showing days remaining out of the demo's total life. */
export const DaysRing = ({
  expiresAt,
  createdAt,
  size = 44,
}: { expiresAt: string; createdAt: string; size?: number }) => {
  const total = Math.max(1, new Date(expiresAt).getTime() - new Date(createdAt).getTime());
  const left = Math.max(0, new Date(expiresAt).getTime() - Date.now());
  const pct = Math.min(1, left / total);
  const days = Math.ceil(left / 86_400_000);
  const r = (size - 6) / 2;
  const c = 2 * Math.PI * r;
  const tone = days <= 2 ? "stroke-destructive" : days <= 4 ? "stroke-warning" : "stroke-primary";
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className="shrink-0"
      role="img"
      aria-label={`${days} days left`}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        className="stroke-muted"
        strokeWidth="4"
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        className={tone}
        strokeWidth="4"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - pct)}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        className="fill-foreground text-[11px] font-semibold"
      >
        {days}d
      </text>
    </svg>
  );
};

/** The red demo-mode banner every demo surface shows. */
export const DemoModeBanner = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "flex items-center justify-center gap-2 bg-destructive px-3 py-1.5 text-center text-xs font-semibold uppercase tracking-wide text-destructive-foreground",
      className,
    )}
  >
    Demo account — payments, SMS and email are disabled
  </div>
);
